import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminBusinesses, adminCreateBusiness, adminUpdateBusiness, adminDeleteBusiness,
  adminSubs, adminApproveSub, adminDeleteSub,
} from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { IconBtn, Badge } from "./ArticlesAdmin";
import { Pencil, Trash2, Check as CheckIcon, Star, ShieldCheck, Clock, Sparkles } from "lucide-react";

const CATS = ["Dining", "Services", "Retail", "Lodging"];
const empty = { name: "", category: "Dining", description: "", phone: "", address: "", website: "", image: "", tier: "standard", sponsored: false, loyalty: false };

export default function DirectoryAdmin() {
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminBusinesses().then(setItems).catch(() => {});
    adminSubs().then(setSubs).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...empty, ...b }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Business name is required.");
    const payload = { name: form.name, category: form.category, description: form.description, phone: form.phone, address: form.address, website: form.website, image: form.image, tier: form.tier, sponsored: form.sponsored, loyalty: !!form.loyalty };
    setBusy(true);
    try {
      if (editing) { await adminUpdateBusiness(editing.id, payload); toast.success("Listing updated."); }
      else { await adminCreateBusiness(payload); toast.success("Listing added."); }
      setOpen(false); load();
    } catch { toast.error("Could not save listing."); }
    finally { setBusy(false); }
  };

  const remove = async (b) => { if (!window.confirm(`Delete "${b.name}"?`)) return; await adminDeleteBusiness(b.id); toast.success("Listing deleted."); load(); };
  const approve = async (s) => { await adminApproveSub(s.id); toast.success(`${s.name} published to directory.`); load(); };
  const rejectSub = async (s) => { await adminDeleteSub(s.id); toast.success("Submission removed."); load(); };

  return (
    <div>
      {subs.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50/60 p-4">
          <h3 className="mb-3 flex items-center gap-2 font-bold text-amber-800"><Clock size={16} /> Pending client submissions ({subs.length})</h3>
          <div className="space-y-2">
            {subs.map((s) => (
              <div key={s.id} data-testid={`sub-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white p-3">
                <div>
                  <p className="font-semibold text-[var(--navy)]">{s.name} <span className="text-xs font-normal text-slate-500">· {s.category}</span></p>
                  <p className="text-xs text-slate-500">{s.description || s.website || s.phone}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approve(s)} data-testid={`approve-${s.id}`} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><CheckIcon size={14} /> Approve & Publish</button>
                  <IconBtn onClick={() => rejectSub(s)} danger testid={`reject-${s.id}`}><Trash2 size={15} /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Toolbar title="Business Directory" subtitle="Manage client & non-client listings, sponsorships and premium placements" onAdd={openNew} addLabel="Add Listing" />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Business</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Tier</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((b) => (
                <tr key={b.id} data-testid={`admin-business-${b.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {b.image && <img src={b.image} alt="" className="h-10 w-14 rounded object-cover" />}
                      <span className="font-semibold text-[var(--navy)]">{b.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{b.category}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {b.tier === "premium" && <Badge icon={ShieldCheck} cls="bg-blue-100 text-blue-700">Premium</Badge>}
                      {b.sponsored && <Badge icon={Star} cls="bg-amber-100 text-amber-700">Sponsored</Badge>}
                      {b.loyalty && <Badge icon={Sparkles} cls="bg-orange-100 text-orange-700">Loyalty Club</Badge>}
                      {b.tier !== "premium" && !b.sponsored && !b.loyalty && <span className="text-xs text-slate-400">Standard · free</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <IconBtn onClick={() => openEdit(b)} testid={`edit-biz-${b.id}`}><Pencil size={15} /></IconBtn>
                      <IconBtn onClick={() => remove(b)} danger testid={`delete-biz-${b.id}`}><Trash2 size={15} /></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {open && (
        <Modal title={editing ? "Edit Listing" : "Add Business Listing"} onClose={() => setOpen(false)} onSubmit={submit} busy={busy}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Business name" full><input data-testid="adm-biz-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
            <Field label="Category"><select data-testid="adm-biz-category" value={form.category} onChange={set("category")} className={inputCls}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Listing tier"><select data-testid="adm-biz-tier" value={form.tier} onChange={set("tier")} className={inputCls}><option value="standard">Standard (free)</option><option value="premium">Premium (verified)</option></select></Field>
            <Field label="Description" full><textarea value={form.description} onChange={set("description")} rows={2} className={inputCls} /></Field>
            <Field label="Phone"><input value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
            <Field label="Website"><input value={form.website} onChange={set("website")} className={inputCls} /></Field>
            <Field label="Address" full><input value={form.address} onChange={set("address")} className={inputCls} /></Field>
            <Field label="Image URL" full><input value={form.image} onChange={set("image")} className={inputCls} /></Field>
            <div className="sm:col-span-2"><Check label="Sponsored (featured placement)" checked={form.sponsored} onChange={toggle("sponsored")} testid="adm-biz-sponsored" /></div>
            <div className="sm:col-span-2"><Check label="Loyalty Club merchant (listed first, with Loyalty Club badge)" checked={!!form.loyalty} onChange={toggle("loyalty")} testid="adm-biz-loyalty" /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
